import React, {
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";

import {
    ArrowLeftOutlined,
    CompressOutlined,
    ExpandOutlined,
    FilePdfOutlined,
    LeftOutlined,
    MenuOutlined,
    MinusOutlined,
    PlusOutlined,
    ReloadOutlined,
    RightOutlined
} from "@ant-design/icons";

import {
    useNavigate
} from "react-router-dom";

import {
    Document,
    Page,
    pdfjs
} from "react-pdf";

import workerSrc
    from "pdfjs-dist/build/pdf.worker.min.js";


pdfjs.GlobalWorkerOptions.workerSrc =
    workerSrc;


const getToken = () =>
    localStorage.getItem("token") ||
    localStorage.getItem("accessToken") ||
    localStorage.getItem("access_token") ||
    "";


const FolderPdfView = ({
    title,
    files = []
}) => {

    const navigate =
        useNavigate();

    const wrapperRef =
        useRef(null);

    const readerRef =
        useRef(null);

    const [selectedId, setSelectedId] =
        useState(
            files?.[0]?.id ||
            null
        );

    const [numPages, setNumPages] =
        useState(0);

    const [pageNumber, setPageNumber] =
        useState(1);

    const [scale, setScale] =
        useState(1.15);

    const [loading, setLoading] =
        useState(true);

    const [error, setError] =
        useState("");

    const [reloadKey, setReloadKey] =
        useState(0);

    const [fullscreen, setFullscreen] =
        useState(false);

    const [menuOpen, setMenuOpen] =
        useState(false);

    const [readerWidth, setReaderWidth] =
        useState(900);


    useEffect(() => {

        if (
            !selectedId &&
            files?.[0]?.id
        ) {
            setSelectedId(
                files[0].id
            );
        }

    }, [
        files,
        selectedId
    ]);


    useEffect(() => {

        const updateWidth =
            () => {

                if (
                    readerRef.current
                ) {
                    setReaderWidth(
                        Math.max(
                            280,
                            readerRef
                                .current
                                .clientWidth -
                            32
                        )
                    );
                }
            };

        updateWidth();

        window.addEventListener(
            "resize",
            updateWidth
        );

        return () =>
            window.removeEventListener(
                "resize",
                updateWidth
            );

    }, []);


    useEffect(() => {

        const handleFullscreen =
            () => {

                setFullscreen(
                    Boolean(
                        document
                            .fullscreenElement
                    )
                );
            };

        document.addEventListener(
            "fullscreenchange",
            handleFullscreen
        );

        return () =>
            document.removeEventListener(
                "fullscreenchange",
                handleFullscreen
            );

    }, []);


    const selectedFile =
        useMemo(
            () =>
                files.find(
                    item =>
                        item.id ===
                        selectedId
                ) ||
                files[0] ||
                null,
            [
                files,
                selectedId
            ]
        );


    const pdfOptions =
        useMemo(
            () => {

                const token =
                    getToken();

                return {
                    url:
                        selectedFile?.url,

                    httpHeaders:
                        token
                            ? {
                                Authorization:
                                    `Bearer ${token}`
                            }
                            : undefined,

                    withCredentials:
                        true
                };

            },
            [
                selectedFile?.url,
                reloadKey
            ]
        );


    const chooseFile =
        file => {

            setSelectedId(
                file.id
            );

            setNumPages(
                0
            );

            setPageNumber(
                1
            );

            setLoading(
                true
            );

            setError(
                ""
            );

            setReloadKey(
                value =>
                    value + 1
            );

            setMenuOpen(
                false
            );
        };


    const reload =
        () => {

            setLoading(
                true
            );

            setError(
                ""
            );

            setReloadKey(
                value =>
                    value + 1
            );
        };


    const toggleFullscreen =
        async () => {

            try {

                if (
                    document
                        .fullscreenElement
                ) {
                    await document
                        .exitFullscreen();

                    return;
                }

                if (
                    wrapperRef.current
                        ?.requestFullscreen
                ) {
                    await wrapperRef
                        .current
                        .requestFullscreen();
                }

            } catch (error) {

                console.log(
                    "Fullscreen error:",
                    error
                );
            }
        };


    const previousPage =
        () => {

            setPageNumber(
                value =>
                    Math.max(
                        1,
                        value - 1
                    )
            );
        };


    const nextPage =
        () => {

            setPageNumber(
                value =>
                    Math.min(
                        numPages || 1,
                        value + 1
                    )
            );
        };


    const zoomOut =
        () => {

            setScale(
                value =>
                    Math.max(
                        0.6,
                        Number(
                            (
                                value -
                                0.15
                            ).toFixed(2)
                        )
                    )
            );
        };


    const zoomIn =
        () => {

            setScale(
                value =>
                    Math.min(
                        2.5,
                        Number(
                            (
                                value +
                                0.15
                            ).toFixed(2)
                        )
                    )
            );
        };


    const FileList =
        ({ mobile = false }) => (

            <div
                className={`
                    h-full
                    flex
                    flex-col
                    bg-[#17191c]
                    border-r
                    border-white/10
                    ${
                        mobile
                            ? "w-[86vw] max-w-[340px]"
                            : "w-[300px]"
                    }
                `}
            >

                <div
                    className="
                        px-4
                        py-4
                        border-b
                        border-white/10
                    "
                >

                    <div
                        className="
                            text-[11px]
                            uppercase
                            tracking-[0.12em]
                            text-gray-500
                            font-semibold
                        "
                    >
                        Tài liệu
                    </div>

                    <div
                        className="
                            mt-1
                            text-sm
                            text-gray-300
                        "
                    >
                        {files.length}
                        {" "}
                        file PDF
                    </div>

                </div>


                <div
                    className="
                        flex-1
                        overflow-y-auto
                        p-2
                    "
                >

                    {files.map(
                        (
                            file,
                            index
                        ) => {

                            const active =
                                selectedFile?.id ===
                                file.id;

                            return (

                                <button
                                    key={
                                        file.id
                                    }
                                    type="button"
                                    onClick={() =>
                                        chooseFile(
                                            file
                                        )
                                    }
                                    className={`
                                        w-full
                                        border-0
                                        rounded-lg
                                        px-3
                                        py-3
                                        mb-1
                                        text-left
                                        flex
                                        items-start
                                        gap-3
                                        transition-colors
                                        ${
                                            active
                                                ? "bg-blue-600 text-white"
                                                : "bg-transparent text-gray-300 hover:bg-white/10"
                                        }
                                    `}
                                >

                                    <FilePdfOutlined
                                        className="
                                            mt-[2px]
                                            flex-none
                                        "
                                    />

                                    <div
                                        className="
                                            min-w-0
                                            flex-1
                                        "
                                    >

                                        <div
                                            className="
                                                text-[13px]
                                                font-semibold
                                                leading-5
                                                break-words
                                            "
                                        >
                                            {file.name ||
                                                `Tài liệu ${index + 1}`}
                                        </div>

                                    </div>

                                </button>
                            );
                        }
                    )}

                </div>

            </div>
        );


    const pageWidth =
        Math.min(
            readerWidth,
            1100
        );


    return (

        <div
            ref={wrapperRef}
            className="
                fixed
                inset-0
                z-[99999]
                bg-[#202124]
                flex
                flex-col
                select-none
            "
            onContextMenu={
                event =>
                    event.preventDefault()
            }
        >

            <header
                className="
                    h-[56px]
                    flex-shrink-0
                    flex
                    items-center
                    gap-1
                    md:gap-2
                    px-2
                    md:px-4
                    bg-[#17191c]
                    border-b
                    border-white/10
                    text-white
                "
            >

                <button
                    type="button"
                    onClick={() =>
                        navigate(-1)
                    }
                    className="
                        h-9
                        px-2
                        md:px-3
                        flex
                        items-center
                        gap-2
                        rounded-lg
                        hover:bg-white/10
                    "
                >
                    <ArrowLeftOutlined />

                    <span
                        className="
                            hidden
                            sm:inline
                            text-sm
                            font-semibold
                        "
                    >
                        THƯ VIỆN
                    </span>
                </button>


                <button
                    type="button"
                    onClick={() =>
                        setMenuOpen(
                            true
                        )
                    }
                    className="
                        md:hidden
                        w-9
                        h-9
                        rounded-lg
                        hover:bg-white/10
                    "
                    title="Danh sách tài liệu"
                >
                    <MenuOutlined />
                </button>


                <div
                    className="
                        flex-1
                        min-w-0
                        px-2
                    "
                >

                    <div
                        className="
                            truncate
                            text-[13px]
                            md:text-[15px]
                            font-semibold
                        "
                    >
                        {title ||
                            "Tài liệu"}
                    </div>

                    <div
                        className="
                            hidden
                            sm:block
                            truncate
                            text-[10px]
                            text-gray-400
                        "
                    >
                        {selectedFile?.name ||
                            "Chọn tài liệu"}
                    </div>

                </div>


                <button
                    type="button"
                    onClick={reload}
                    className="
                        w-9
                        h-9
                        rounded-lg
                        hover:bg-white/10
                    "
                    title="Tải lại"
                >
                    <ReloadOutlined />
                </button>


                <button
                    type="button"
                    onClick={
                        toggleFullscreen
                    }
                    className="
                        w-9
                        h-9
                        rounded-lg
                        hover:bg-white/10
                    "
                    title="Toàn màn hình"
                >
                    {fullscreen
                        ? <CompressOutlined />
                        : <ExpandOutlined />
                    }
                </button>

            </header>


            <div
                className="
                    flex-1
                    min-h-0
                    flex
                "
            >

                <aside
                    className="
                        hidden
                        md:block
                        h-full
                    "
                >
                    <FileList />
                </aside>


                <main
                    ref={readerRef}
                    className="
                        relative
                        flex-1
                        min-w-0
                        h-full
                        overflow-auto
                        bg-[#202124]
                    "
                >

                    {!selectedFile?.url ? (

                        <div
                            className="
                                absolute
                                inset-0
                                flex
                                items-center
                                justify-center
                                text-gray-300
                            "
                        >
                            Không tìm thấy PDF trong thư mục.
                        </div>

                    ) : (

                        <div
                            className="
                                min-h-full
                                flex
                                justify-center
                                px-3
                                py-5
                                md:px-5
                            "
                        >

                            <Document
                                key={
                                    `${selectedFile.id}-${reloadKey}`
                                }
                                file={
                                    pdfOptions
                                }
                                loading={
                                    <div
                                        className="
                                            text-gray-300
                                            py-20
                                        "
                                    >
                                        Đang mở tài liệu...
                                    </div>
                                }
                                error={
                                    <div
                                        className="
                                            bg-white
                                            rounded-xl
                                            p-6
                                            mt-20
                                            text-gray-800
                                            shadow-xl
                                            min-w-[280px]
                                            max-w-[520px]
                                        "
                                    >
                                        <div
                                            className="
                                                text-lg
                                                font-semibold
                                            "
                                        >
                                            Lỗi
                                        </div>

                                        <div
                                            className="
                                                mt-3
                                                text-sm
                                                text-gray-600
                                            "
                                        >
                                            {error ||
                                                "Không tải được tài liệu PDF."}
                                        </div>

                                        <button
                                            type="button"
                                            onClick={
                                                reload
                                            }
                                            className="
                                                mt-5
                                                bg-blue-600
                                                text-white
                                                rounded-full
                                                px-5
                                                py-2
                                                border-0
                                                font-semibold
                                            "
                                        >
                                            Tải lại
                                        </button>
                                    </div>
                                }
                                onLoadSuccess={
                                    ({
                                        numPages:
                                            total
                                    }) => {

                                        setNumPages(
                                            total
                                        );

                                        setPageNumber(
                                            1
                                        );

                                        setLoading(
                                            false
                                        );

                                        setError(
                                            ""
                                        );
                                    }
                                }
                                onLoadError={
                                    err => {

                                        setLoading(
                                            false
                                        );

                                        setError(
                                            err?.message ||
                                            "Không tải được tài liệu PDF."
                                        );

                                        console.error(
                                            "PDFJS LOAD ERROR:",
                                            err
                                        );
                                    }
                                }
                            >

                                <Page
                                    pageNumber={
                                        pageNumber
                                    }
                                    width={
                                        pageWidth
                                    }
                                    scale={
                                        scale
                                    }
                                    renderTextLayer={
                                        false
                                    }
                                    renderAnnotationLayer={
                                        false
                                    }
                                    loading={
                                        <div
                                            className="
                                                text-gray-300
                                                py-20
                                            "
                                        >
                                            Đang tải trang...
                                        </div>
                                    }
                                />

                            </Document>

                        </div>
                    )}

                </main>

            </div>


            {selectedFile?.url &&
                numPages > 0 && (

                <div
                    className="
                        fixed
                        z-[100001]
                        bottom-4
                        left-1/2
                        -translate-x-1/2
                        flex
                        items-center
                        gap-1
                        bg-[#303134]/95
                        backdrop-blur
                        text-white
                        rounded-full
                        px-2
                        py-2
                        shadow-2xl
                        border
                        border-white/10
                    "
                >

                    <button
                        type="button"
                        onClick={
                            previousPage
                        }
                        disabled={
                            pageNumber <= 1
                        }
                        className="
                            w-9
                            h-9
                            rounded-full
                            disabled:opacity-30
                            hover:bg-white/10
                        "
                        title="Trang trước"
                    >
                        <LeftOutlined />
                    </button>


                    <div
                        className="
                            px-2
                            text-sm
                            whitespace-nowrap
                        "
                    >
                        Trang
                        {" "}
                        <strong>
                            {pageNumber}
                        </strong>
                        {" / "}
                        {numPages}
                    </div>


                    <button
                        type="button"
                        onClick={
                            nextPage
                        }
                        disabled={
                            pageNumber >=
                            numPages
                        }
                        className="
                            w-9
                            h-9
                            rounded-full
                            disabled:opacity-30
                            hover:bg-white/10
                        "
                        title="Trang sau"
                    >
                        <RightOutlined />
                    </button>


                    <div
                        className="
                            w-px
                            h-7
                            bg-white/20
                            mx-1
                        "
                    />


                    <button
                        type="button"
                        onClick={
                            zoomOut
                        }
                        className="
                            w-9
                            h-9
                            rounded-full
                            hover:bg-white/10
                        "
                        title="Thu nhỏ"
                    >
                        <MinusOutlined />
                    </button>


                    <div
                        className="
                            min-w-[48px]
                            text-center
                            text-xs
                        "
                    >
                        {Math.round(
                            scale * 100
                        )}
                        %
                    </div>


                    <button
                        type="button"
                        onClick={
                            zoomIn
                        }
                        className="
                            w-9
                            h-9
                            rounded-full
                            hover:bg-white/10
                        "
                        title="Phóng to"
                    >
                        <PlusOutlined />
                    </button>

                </div>
            )}


            {menuOpen && (

                <div
                    className="
                        md:hidden
                        fixed
                        inset-0
                        z-[100002]
                        flex
                    "
                >

                    <div
                        className="
                            h-full
                            shadow-2xl
                        "
                    >
                        <FileList mobile />
                    </div>

                    <button
                        type="button"
                        aria-label="Đóng danh sách"
                        onClick={() =>
                            setMenuOpen(
                                false
                            )
                        }
                        className="
                            flex-1
                            border-0
                            bg-black/60
                        "
                    />

                </div>
            )}

        </div>
    );
};


export default FolderPdfView;
